package org.teddapp.project.ui.finance

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.tedd.db.Expense_list
import com.tedd.db.Asset
import com.tedd.db.Asset_valuation
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import com.tedd.db.Simulation
import com.tedd.db.Simulation_line
import kotlinx.coroutines.CoroutineScope
import org.teddapp.project.db.repo.LoanDirection
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.finance.FinanceScheduler
import org.teddapp.project.ui.TeddSnackbarState

/**
 * État et actions de la section Finance, indépendants de la mise en page.
 * Les données sont alimentées par [rememberFinanceModel] ; les deux mises en
 * page lisent le même objet.
 */
@Stable
internal class FinanceModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    val snackbar: TeddSnackbarState,
    private val core: CoreSession? = null,
) {

    /** Les listes observées, chaudes cinq secondes après le départ de l'écran : y revenir ne relit rien. */
    private fun <T> hot(flow: kotlinx.coroutines.flow.Flow<List<T>>) =
        flow.stateIn(scope, SharingStarted.WhileSubscribed(org.teddapp.project.ui.ScreenModels.HOT_MS), emptyList())
    internal val accountsFlow = hot(repos.financeAccounts.observeAll())
    internal val categoriesFlow = hot(repos.financeCategories.observeAll())
    internal val transactionsFlow = hot(repos.financeTxns.observeAll())
    internal val expenseListsFlow = hot(repos.expenseLists.observeAll())
    internal val expenseListItemsFlow = hot(repos.expenseLists.observeAllItems())
    internal val expenseEntriesFlow = hot(repos.expenseLists.observeAllEntries())
    internal val assetsFlow = hot(repos.assets.observeAll())
    internal val valuationsFlow = hot(repos.assets.observeValuations())
    internal val simulationsFlow = hot(repos.simulations.observeAll())
    internal val simulationLinesFlow = hot(repos.simulations.observeAllLines())
    internal val loansFlow = hot(repos.loans.observeAll())
    internal val loanPaymentsFlow = hot(repos.loans.observeAllPayments())
    // ── Données ────────────────────────────────────────────────────
    var accounts by mutableStateOf<List<Finance_account>>(emptyList())
    var categories by mutableStateOf<List<Finance_category>>(emptyList())
    var transactions by mutableStateOf<List<Finance_transaction>>(emptyList())
    var expenseLists by mutableStateOf<List<Expense_list>>(emptyList())
    var expenseListItems by mutableStateOf<List<Expense_list_item>>(emptyList())
    var assets by mutableStateOf<List<Asset>>(emptyList())
    var valuations by mutableStateOf<List<Asset_valuation>>(emptyList())
    var simulations by mutableStateOf<List<Simulation>>(emptyList())
    var simulationLines by mutableStateOf<List<Simulation_line>>(emptyList())
    var loans by mutableStateOf<List<Loan>>(emptyList())
    var loanPayments by mutableStateOf<List<Loan_payment>>(emptyList())

    // ── Sélection et filtres ───────────────────────────────────────
    var selectedAccountId by mutableStateOf<String?>(null)

    /**
     * Compte ouvert par défaut en arrivant sur Finance.
     *
     * Gardé sur l'appareil et non synchronisé : on saisit ses dépenses courantes
     * depuis le téléphone et on regarde ses comptes depuis l'ordinateur, donc le
     * bon défaut n'est pas le même des deux côtés.
     */
    var defaultAccountId by mutableStateOf<String?>(null)
        private set

    /** Charge le compte par défaut au premier affichage. */
    suspend fun loadDefaultAccount() {
        val stored = repos.preferences.get(PREF_DEFAULT_ACCOUNT)
        defaultAccountId = stored
        if (selectedAccountId == null && stored != null && accounts.any { it.id == stored }) {
            selectedAccountId = stored
        }
    }

    /** Retient ce compte, ou l'oublie s'il l'était déjà. */
    fun toggleDefaultAccount(accountId: String) {
        val nouveau = if (defaultAccountId == accountId) null else accountId
        defaultAccountId = nouveau
        scope.launch {
            if (nouveau == null) repos.preferences.delete(PREF_DEFAULT_ACCOUNT)
            else repos.preferences.set(PREF_DEFAULT_ACCOUNT, nouveau)
        }
    }
    var tab by mutableStateOf(FinanceTab.Transactions)
    var txFilter by mutableStateOf(TxFilter.All)
    var txPeriod by mutableStateOf(TxPeriod.ThisMonth)
    var txCategoryId by mutableStateOf<String?>(null)
    var search by mutableStateOf("")
    var expandedListId by mutableStateOf<String?>(null)

    // ── Dialogues ──────────────────────────────────────────────────
    var showAdd by mutableStateOf(false)
    var editingTx by mutableStateOf<Finance_transaction?>(null)
    var confirmDeleteTx by mutableStateOf<Finance_transaction?>(null)
    var markingItem by mutableStateOf<Expense_list_item?>(null)

    /** Les sous-dépenses de toutes les lignes, et la ligne dont le portefeuille est ouvert. */
    var expenseEntries by mutableStateOf<List<com.tedd.db.Expense_entry>>(emptyList())
    var walletItem by mutableStateOf<Expense_list_item?>(null)

    fun addEntry(item: Expense_list_item, label: String, amount: Long, date: String?, note: String?) {
        scope.launch { repos.expenseLists.addEntry(item.id, label, amount, date, note) }
    }

    /** Une sous-dépense payée tout de suite depuis [accountId] : le dépôt écrit tout d'un bloc. */
    fun addEntryPaid(item: Expense_list_item, label: String, amount: Long, date: String?, accountId: String) {
        scope.launch { repos.expenseLists.addEntryPaid(item, label, amount, date, null, accountId) }
    }

    /** Ce que les sous-dépenses d'une ligne ont déjà payé, pour le panneau qui la coche. */
    fun paidForItem(itemId: String): Long =
        expenseEntries.filter { it.item_id == itemId && it.is_deleted == 0L && it.tx_id != null }.sumOf { it.amount }

    fun updateEntry(entry: com.tedd.db.Expense_entry, label: String, amount: Long, date: String?, note: String?) {
        scope.launch { repos.expenseLists.updateEntry(entry, label, amount, date, note) }
    }

    fun deleteEntry(entry: com.tedd.db.Expense_entry) {
        scope.launch { repos.expenseLists.deleteEntry(entry) }
    }
    var editingAccount by mutableStateOf<Finance_account?>(null)
    var creatingAccount by mutableStateOf(false)
    var editingList by mutableStateOf<Expense_list?>(null)
    var creatingList by mutableStateOf(false)
    var categoryManagerOpen by mutableStateOf(false)

    /**
     * Crée une catégorie à la volée et rend son identifiant, pour la sélectionner
     * aussitôt. Couleur et icône reprennent celles du reste : on nomme, on ne
     * décore pas — le gestionnaire du tableau de bord sert à ça.
     */
    suspend fun createCategory(name: String, type: String): String? {
        val propre = name.trim()
        if (propre.isEmpty()) return null
        val deja = categories.firstOrNull {
            it.is_deleted == 0L && it.type == type && it.name.equals(propre, ignoreCase = true)
        }
        if (deja != null) return deja.id
        return repos.financeCategories.create(
            name = propre,
            type = type,
            icon = if (type == "income") "arrow-down" else "tag",
            color = "#6F7D8C",
            orderIndex = categories.size.toLong(),
        )
    }
    var creatingAsset by mutableStateOf(false)
    var editingAsset by mutableStateOf<Asset?>(null)
    var valuingAsset by mutableStateOf<Asset?>(null)
    var creatingLoan by mutableStateOf(false)
    var editingLoan by mutableStateOf<Loan?>(null)
    var repayingLoan by mutableStateOf<Loan?>(null)
    var viewingLoanPayments by mutableStateOf<Loan?>(null)
    var confirmDeleteLoan by mutableStateOf<Loan?>(null)
    /** Dépense majeure en attente du filtre cartésien (Descartes). */
    var pendingExpense by mutableStateOf<PendingExpense?>(null)

    val selectedId: String?
        get() = selectedAccountId
            ?: defaultAccountId?.takeIf { id -> accounts.any { it.id == id } }
            ?: accounts.firstOrNull()?.id
    val account: Finance_account? get() = accounts.firstOrNull { it.id == selectedId }
    val activeAccounts: List<Finance_account> get() = accounts.filter { it.is_deleted == 0L }
    val currency: String get() = account?.currency ?: "XOF"
    val accTxns: List<Finance_transaction>
        get() {
            val id = selectedId ?: return emptyList()
            return transactions.filter { it.account_id == id || it.to_account_id == id }
        }

    // ── Actions ────────────────────────────────────────────────────
    fun toggleExpanded(listId: String) { expandedListId = if (expandedListId == listId) null else listId }

    fun sendItemToPomodoro(item: Expense_list_item) {
        val title = "Payer : ${item.name} (${fmtMoney(item.planned, currency, compact = true)})"
        scope.launch {
            val session = repos.pomodoro.openTodaySession(TODAY_ISO)
            val dup = repos.pomodoroItems.listBySession(session.id)
                .any { it.source_type == "quick" && it.custom_title == title && it.is_deleted == 0L }
            if (dup) {
                snackbar.show("Déjà dans la liste Pomodoro")
            } else {
                repos.pomodoro.addQuick(TODAY_ISO, title)
                snackbar.show("Ajouté au Pomodoro du jour")
            }
        }
    }

    /** Modifie un élément de liste : nom, montant prévu, catégorie. */
    fun editItem(item: Expense_list_item, name: String, planned: Long, catId: String?) {
        scope.launch {
            repos.expenseLists.updateItem(
                id = item.id, name = name.trim(), planned = planned, actual = item.actual_,
                catId = catId, isDone = item.is_done == 1L, orderIndex = item.order_index,
            )
        }
    }

    /** Duplique une liste et ses éléments, cases décochées. */
    fun cloneList(list: Expense_list) {
        scope.launch {
            val id = repos.expenseLists.cloneList(list.id, "${list.name} (copie)")
            snackbar.show(if (id != null) "Liste dupliquée" else "Duplication impossible")
        }
    }

    // ── Patrimoine ─────────────────────────────────────────────────
    fun saveAsset(existing: Asset?, name: String, kind: String, currency: String, value: Long?, purchasePrice: Long?, acquired: String?, note: String?) {
        scope.launch {
            if (existing == null) {
                repos.assets.create(
                    name = name, kind = kind, currency = currency, value = value,
                    purchasePrice = purchasePrice, acquiredDate = acquired, note = note,
                    orderIndex = assets.size.toLong(),
                )
            } else {
                repos.assets.update(
                    id = existing.id, name = name, kind = kind, currency = currency, value = value,
                    purchasePrice = purchasePrice, acquiredDate = acquired, valuedAt = existing.valued_at,
                    note = note, archived = existing.archived != 0L, orderIndex = existing.order_index,
                )
            }
        }
        creatingAsset = false
        editingAsset = null
    }

    fun recordValuation(asset: Asset, value: Long, date: String, note: String?) {
        scope.launch { repos.assets.recordValuation(asset.id, value, date, note) }
        valuingAsset = null
        snackbar.show("Valeur constatée")
    }

    fun deleteAsset(asset: Asset) {
        scope.launch { repos.assets.softDelete(asset.id) }
    }

    // ── Prêts ──────────────────────────────────────────────────────
    fun createLoan(direction: String, counterparty: String, principal: Long, interest: Long, date: String, dueDate: String?, note: String?, accountId: String?) {
        creatingLoan = false
        val currency = accounts.firstOrNull { it.id == accountId }?.currency ?: this.currency
        scope.launch { repos.loans.create(direction, counterparty, principal, interest, currency, date, dueDate, note, accountId) }
    }

    fun updateLoan(loan: Loan, counterparty: String, interest: Long, dueDate: String?, note: String?) {
        editingLoan = null
        scope.launch { repos.loans.update(loan.id, counterparty, interest, dueDate, note) }
    }

    /** Un remboursement ; le dépôt refuse ce qui dépasse le restant. */
    fun repayLoan(loan: Loan, amount: Long, date: String, accountId: String, note: String?) {
        repayingLoan = null
        scope.launch {
            val id = repos.loans.addPayment(loan.id, amount, date, accountId, note)
            snackbar.show(if (id == null) "Le remboursement dépasse le restant" else if (loan.direction == LoanDirection.LENT) "Remboursement reçu" else "Remboursement enregistré")
        }
    }

    fun deleteLoanPayment(payment: Loan_payment) {
        scope.launch { repos.loans.deletePayment(payment) }
    }

    /** Supprime le prêt, ses remboursements et leurs mouvements, sans corbeille. */
    fun deleteLoan(loan: Loan) {
        confirmDeleteLoan = null
        scope.launch { repos.loans.delete(loan.id) }
    }

    fun untickItem(itemId: String) { scope.launch { repos.expenseLists.toggleItemDone(itemId) } }
    fun deleteItem(itemId: String) { scope.launch { repos.expenseLists.softDeleteItem(itemId) } }
    fun addItem(listId: String, name: String, planned: Long, catId: String?, currentCount: Int) {
        scope.launch {
            repos.expenseLists.createItem(listId = listId, name = name, planned = planned, catId = catId, orderIndex = currentCount.toLong())
        }
    }

    fun createTx(accountId: String, date: String, type: String, amount: Long, description: String?, catId: String?, toAccountId: String?) {
        // Dépense majeure : le filtre cartésien s'affiche avant d'écrire quoi que ce soit.
        val balance = accounts.firstOrNull { it.id == accountId }?.balance ?: 0L
        if (type == "expense" && needsCartesianFilter(amount, balance)) {
            pendingExpense = PendingExpense(accountId, date, amount, description, catId, currency)
            return
        }
        scope.launch {
            repos.financeTxns.create(
                date = date, accountId = accountId, toAccountId = toAccountId, catId = catId, type = type,
                amount = amount, description = description, fundId = null,
            )
        }
    }

    /** Besoin réel : la dépense est enregistrée, le verdict avec elle. */
    fun confirmPendingAsNeed(verdict: String = Verdict.NEED) {
        val pe = pendingExpense ?: return
        pendingExpense = null
        scope.launch {
            val id = repos.financeTxns.create(
                date = pe.date, accountId = pe.accountId, toAccountId = null, catId = pe.catId, type = "expense",
                amount = pe.amount, description = pe.description, fundId = null,
            )
            repos.financeTxns.setVerdict(id, verdict)
            recordExpense(id, pe, verdict)
        }
    }

    /** Désir maîtrisé : la dépense n'a pas lieu. On garde la trace de la décision, montant à zéro. */
    fun masterPendingDesire() {
        val pe = pendingExpense ?: return
        pendingExpense = null
        scope.launch {
            val id = repos.financeTxns.create(
                date = pe.date, accountId = pe.accountId, toAccountId = null, catId = pe.catId, type = "expense",
                amount = 0L, description = buildString {
                    append(pe.description?.takeIf { it.isNotBlank() } ?: "Dépense annulée")
                    append(" · désir maîtrisé (")
                    append(fmtMoney(pe.amount, pe.currency, compact = true))
                    append(")")
                },
                fundId = null,
            )
            repos.financeTxns.setVerdict(id, Verdict.MASTERED)
            recordExpense(id, pe, Verdict.MASTERED)
            snackbar.show("Désir maîtrisé · décision réussie")
        }
    }

    fun dismissPendingExpense() { confirmPendingAsNeed(Verdict.IMPULSE) }

    /**
     * Écrit la dépense dans le journal du noyau, avec son verdict. Un désir maîtrisé
     * n'a coûté aucun argent : le montant transmis est nul, mais la décision compte.
     */
    private fun recordExpense(txId: String, pending: PendingExpense, verdict: String) {
        val category = categories.firstOrNull { it.id == pending.catId }?.name ?: "non classé"
        core?.record(
            userId = TeddIdentity.USER_ID,
            eventJson = CoreFacts.expenseLogged(
                id = txId,
                amount = if (verdict == Verdict.MASTERED) 0.0 else pending.amount.toDouble(),
                currency = pending.currency,
                category = category,
                rational = verdict != Verdict.IMPULSE,
                justification = when (verdict) {
                    Verdict.NEED -> "Besoin réel"
                    Verdict.MASTERED -> "Désir maîtrisé, dépense annulée"
                    else -> "Enregistrée sans réponse au filtre"
                },
            ),
        )
    }

    /** Taux de décisions rationnelles du mois courant, ou null si aucun verdict. */
    val monthRationalRate: Int?
        get() = rationalRate(transactions.filter { it.is_deleted == 0L && it.date in MONTH_START..MONTH_END }.map { it.verdict })

    fun updateTx(old: Finance_transaction, date: String, type: String, amount: Long, description: String?, catId: String?, toAccountId: String?) {
        scope.launch {
            repos.financeTxns.update(
                old = old, date = date, accountId = old.account_id, toAccountId = toAccountId, catId = catId, type = type,
                amount = amount, description = description, fundId = null,
            )
        }
    }

    fun deleteTx(tx: Finance_transaction) {
        scope.launch { repos.financeTxns.softDelete(tx) }
        confirmDeleteTx = null
        editingTx = null
    }

    /** Coche la ligne ; le dépôt ne débite que ce que les sous-dépenses n'ont pas déjà payé. */
    fun markItemDone(item: Expense_list_item, actual: Long, accountId: String, date: String, description: String) {
        scope.launch { repos.expenseLists.markDone(item, actual, accountId, date, description) }
        markingItem = null
    }

    fun deleteAccount(acc: Finance_account) {
        scope.launch { repos.financeAccounts.softDelete(acc.id) }
        if (selectedId == acc.id) selectedAccountId = null
        editingAccount = null
    }

    fun deleteList(list: Expense_list) {
        scope.launch { repos.expenseLists.softDeleteList(list.id) }
        editingList = null
    }

    // ── Simulation ─────────────────────────────────────────────────
    var selectedSimulationId by mutableStateOf<String?>(null)
    var creatingSimulation by mutableStateOf(false)
    var renamingSimulation by mutableStateOf<Simulation?>(null)
    var confirmDeleteSimulation by mutableStateOf<Simulation?>(null)

    /** Le brouillon ouvert : celui choisi, sinon le premier. */
    val selectedSimulation: Simulation?
        get() = simulations.firstOrNull { it.id == selectedSimulationId } ?: simulations.firstOrNull()

    fun createSimulation(name: String, note: String?) {
        creatingSimulation = false
        scope.launch { selectedSimulationId = repos.simulations.create(name, note) }
    }

    fun renameSimulation(sim: Simulation, name: String, note: String?) {
        renamingSimulation = null
        scope.launch { repos.simulations.rename(sim.id, name, note) }
    }

    fun duplicateSimulation(sim: Simulation) {
        scope.launch {
            val id = repos.simulations.duplicate(sim.id, "${sim.name} (copie)")
            if (id != null) selectedSimulationId = id
            snackbar.show(if (id != null) "Brouillon dupliqué" else "Duplication impossible")
        }
    }

    /** Supprime le brouillon et ses lignes, sans corbeille. */
    fun deleteSimulation(sim: Simulation) {
        confirmDeleteSimulation = null
        if (selectedSimulationId == sim.id) selectedSimulationId = null
        scope.launch { repos.simulations.delete(sim.id) }
    }

    fun addSimulationLine(simulationId: String, label: String, expression: String) {
        scope.launch { repos.simulations.addLine(simulationId, label, expression) }
    }

    fun editSimulationLine(line: Simulation_line, label: String, expression: String) {
        scope.launch { repos.simulations.updateLine(line.id, label, expression) }
    }

    fun deleteSimulationLine(line: Simulation_line) {
        scope.launch { repos.simulations.deleteLine(line.id) }
    }

    fun moveSimulationLine(line: Simulation_line, delta: Int) {
        scope.launch { repos.simulations.moveLine(line.id, delta) }
    }
}

/** Construit et alimente le [FinanceModel] ; rejoue les échéances dues à l'ouverture. */
@Composable
internal fun rememberFinanceModel(repos: TeddRepositories): FinanceModel {
    val core = LocalCoreSession.current
    // Gardé d'une rubrique à l'autre, avec ses listes chaudes : revenir ne relit rien.
    val model = org.teddapp.project.ui.rememberScreenModel(repos, core, "finance") { s -> FinanceModel(repos, s, TeddSnackbarState(), core) }
    LaunchedEffect(Unit) { FinanceScheduler.runDue(repos) }
    // Après le chargement des comptes : sans eux, le défaut ne se vérifie pas.
    LaunchedEffect(model.accounts.size) { model.loadDefaultAccount() }
    model.accounts = model.accountsFlow.collectAsState().value
    model.categories = model.categoriesFlow.collectAsState().value
    model.transactions = model.transactionsFlow.collectAsState().value
    model.expenseLists = model.expenseListsFlow.collectAsState().value
    model.expenseListItems = model.expenseListItemsFlow.collectAsState().value
    model.expenseEntries = model.expenseEntriesFlow.collectAsState().value
    model.assets = model.assetsFlow.collectAsState().value
    model.valuations = model.valuationsFlow.collectAsState().value
    model.simulations = model.simulationsFlow.collectAsState().value
    model.simulationLines = model.simulationLinesFlow.collectAsState().value
    model.loans = model.loansFlow.collectAsState().value
    model.loanPayments = model.loanPaymentsFlow.collectAsState().value
    return model
}

/** Clé du compte par défaut dans les préférences de l'appareil. */
private const val PREF_DEFAULT_ACCOUNT = "finance.default_account"
